import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Breadcrumbs, Chip, CircularProgress, Link, Sheet, Grid, Tabs, TabList, Tab, TabPanel, tabClasses, Button, Modal, ModalDialog, ModalClose, IconButton } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { addDays, format as formatDateFns, startOfYear, endOfYear, subYears, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval, getDay, isSameDay } from 'date-fns';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import { enGB } from 'date-fns/locale';
import { DateRangePicker, RangeKeyDict, Range, defaultStaticRanges } from 'react-date-range';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement,
    TimeScale,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

import ApiService from "../../axios/Bookings-Axios";
import GeneralApiService from "../../axios/Axios";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

interface ApiStatisticData {
    dateRange: { startDate: string; endDate: string };
    summary: {
        totalBookingsScheduled: number;
        totalNewBookingsCreated: number;
        totalNewBookingsRevenue: number;
        totalLeads: number;
        conversionRate: number;
        totalRevenue: number;
        avgBookingValue: number;
        leadsPotentialRevenue: number;
        activeRecurringRevenueTotal: number;
        newRecurringBookingsCount: number;
        newRecurringBookingsValue: number;
        newMessages: number;
        cancelledJobsCount: number;
        needsReschedulingJobsCount: number;
    };
    revenueDetails: {
        dailyTrend: { date: string; value: number }[];
        byService: { service_name: string; total_revenue: number; count: number }[];
        byPaymentType: { payment_type_name: string; total_revenue: number; count: number }[];
    };
    leadDetails: {
        dailyTrend: { date: string; count: number }[];
        byAdvertisingPlatform: { platform_name: string; count: number; converted_count: number }[];
        byFormType: { form_type: string; count: number; converted_count: number }[];
        conversionSources: { source: string; count: number }[];
    };
    bookingDetails: {
        dailyTrend: { date: string; count: number }[];
        byJobStatus: { status: string; count: number }[];
        completionRate: number;
    };
    operationalMetrics: {
        gutterCerts: { sent: number; total_eligible: number };
        reviewRequests: { enabled: number; total_completed: number };
    };
    callMetrics: {
        totalOutboundCalls: number;
        totalInboundCalls: number;
        avgSpeedToLeadSeconds: number | null;
    };
}

const initialStatistics: ApiStatisticData = {
    dateRange: { startDate: '', endDate: '' },
    summary: {
        totalBookingsScheduled: 0, totalNewBookingsCreated: 0, totalNewBookingsRevenue: 0,
        totalLeads: 0, conversionRate: 0, totalRevenue: 0,
        avgBookingValue: 0, leadsPotentialRevenue: 0, activeRecurringRevenueTotal: 0,
        newRecurringBookingsCount: 0, newRecurringBookingsValue: 0, newMessages: 0,
        cancelledJobsCount: 0, needsReschedulingJobsCount: 0,
    },
    revenueDetails: { dailyTrend: [], byService: [], byPaymentType: [] },
    leadDetails: { dailyTrend: [], byAdvertisingPlatform: [], byFormType: [], conversionSources: [] },
    bookingDetails: { dailyTrend: [], byJobStatus: [], completionRate: 0 },
    operationalMetrics: { gutterCerts: { sent: 0, total_eligible: 0 }, reviewRequests: { enabled: 0, total_completed: 0 } },
    callMetrics: { totalOutboundCalls: 0, totalInboundCalls: 0, avgSpeedToLeadSeconds: null }
};

interface MarketingBalanceSummary {
    by_type: { type: string; total_amount: number; count: number }[];
    by_service: { service: string; total_amount: number; count: number }[];
    by_type_and_service: { type: string; service: string; total_amount: number; count: number }[];
    totals: {
        charges: { total: number; count: number };
        top_ups: { total: number; count: number };
    };
}

interface AdsActivityDay {
    date: string;
    facebook: boolean;
    google: boolean;
}

interface AdsActivityResponse {
    year: number;
    month: number;
    days: AdsActivityDay[];
}

const initialMarketingBalance: MarketingBalanceSummary = {
    by_type: [],
    by_service: [],
    by_type_and_service: [],
    totals: {
        charges: { total: 0, count: 0 },
        top_ups: { total: 0, count: 0 }
    }
};


interface DateRangeStateItem extends Range { }

const customStaticRanges = [
    ...defaultStaticRanges,
    {
        label: 'This Year',
        range: () => ({
            startDate: startOfYear(new Date()),
            endDate: new Date()
        }),
        isSelected: (range: Range) => {
            const thisYearStart = startOfYear(new Date());
            return range.startDate?.getTime() === thisYearStart.getTime() &&
                   range.endDate?.toDateString() === new Date().toDateString();
        }
    },
    {
        label: 'Last Year',
        range: () => ({
            startDate: startOfYear(subYears(new Date(), 1)),
            endDate: endOfYear(subYears(new Date(), 1))
        }),
        isSelected: (range: Range) => {
            const lastYearStart = startOfYear(subYears(new Date(), 1));
            const lastYearEnd = endOfYear(subYears(new Date(), 1));
            return range.startDate?.getTime() === lastYearStart.getTime() &&
                   range.endDate?.getTime() === lastYearEnd.getTime();
        }
    }
];

const KpiCard = ({ title, value, prefix = '', suffix = '' }: { title: string; value: string | number; prefix?: string; suffix?: string }) => (
    <Sheet
        variant="outlined"
        sx={{
            p: 2,
            borderRadius: 'sm',
            textAlign: 'center',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}
    >
        <Typography level="title-sm" textColor="text.secondary">{title}</Typography>
        <Typography level="h3">{prefix}{value}{suffix}</Typography>
    </Sheet>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Sheet
        variant="outlined"
        sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 'sm',
            height: { xs: '300px', sm: '400px' },
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}
    >
        <Typography level="title-md" mb={1} sx={{ fontSize: { xs: 'sm', sm: 'md' } }}>{title}</Typography>
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                {children}
            </Box>
        </Box>
    </Sheet>
);

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface AdsActivityCalendarProps {
    month: Date;
    onMonthChange: (next: Date) => void;
    data: AdsActivityResponse | null;
    loading: boolean;
}

const AdsActivityCalendar: React.FC<AdsActivityCalendarProps> = ({ month, onMonthChange, data, loading }) => {
    const today = new Date();
    const currentMonthStart = startOfMonth(today);
    const canGoForward = startOfMonth(month).getTime() < currentMonthStart.getTime();

    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Build a lookup keyed by ISO date string -> activity for the day.
    const activityByDate = new Map<string, AdsActivityDay>();
    (data?.days || []).forEach(day => {
        activityByDate.set(day.date, day);
    });

    // Pad the grid so the first day lands in the right weekday column (Mon-start).
    const firstDayOfWeek = (getDay(monthStart) + 6) % 7; // 0 = Monday
    const leadingBlanks: null[] = Array(firstDayOfWeek).fill(null);
    const cells: (Date | null)[] = [...leadingBlanks, ...daysInMonth];
    while (cells.length % 7 !== 0) cells.push(null);

    const onColor = 'rgba(46, 160, 67, 0.85)';
    const offColor = 'rgba(218, 54, 51, 0.75)';
    const futureColor = 'rgba(0, 0, 0, 0.04)';

    return (
        <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', maxWidth: 560 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                <Typography level="title-sm">Ads Activity</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                        variant="plain"
                        size="sm"
                        onClick={() => onMonthChange(startOfMonth(subMonths(month, 1)))}
                        aria-label="Previous month"
                    >
                        <ChevronLeftRoundedIcon />
                    </IconButton>
                    <Typography level="body-sm" sx={{ minWidth: 110, textAlign: 'center', fontWeight: 600 }}>
                        {formatDateFns(month, 'MMMM yyyy')}
                    </Typography>
                    <IconButton
                        variant="plain"
                        size="sm"
                        disabled={!canGoForward}
                        onClick={() => onMonthChange(startOfMonth(addMonths(month, 1)))}
                        aria-label="Next month"
                    >
                        <ChevronRightRoundedIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: onColor }} />
                    <Typography level="body-xs">Ads on</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: offColor }} />
                    <Typography level="body-xs">Ads off</Typography>
                </Box>
                <Typography level="body-xs" textColor="text.tertiary">F = Facebook · G = Google</Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
                    <CircularProgress size="sm" />
                </Box>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
                    {WEEKDAY_LABELS.map(label => (
                        <Typography
                            key={label}
                            level="body-xs"
                            textColor="text.tertiary"
                            sx={{ textAlign: 'center', fontWeight: 600, fontSize: '10px', py: 0.25 }}
                        >
                            {label}
                        </Typography>
                    ))}
                    {cells.map((cellDate, idx) => {
                        if (!cellDate) {
                            return <Box key={`blank-${idx}`} sx={{ aspectRatio: '1 / 1' }} />;
                        }

                        const iso = formatDateFns(cellDate, 'yyyy-MM-dd');
                        const activity = activityByDate.get(iso);
                        const isFuture = cellDate > today;
                        const isToday = isSameDay(cellDate, today);

                        const facebookOn = activity?.facebook ?? false;
                        const googleOn = activity?.google ?? false;
                        const anyOn = facebookOn || googleOn;

                        let platformLabel = '';
                        if (facebookOn && googleOn) platformLabel = 'F+G';
                        else if (facebookOn) platformLabel = 'F';
                        else if (googleOn) platformLabel = 'G';

                        const bgColor = isFuture ? futureColor : (anyOn ? onColor : offColor);

                        const tooltip = isFuture
                            ? `${formatDateFns(cellDate, 'EEE d MMM yyyy')} (upcoming)`
                            : `${formatDateFns(cellDate, 'EEE d MMM yyyy')}\nFacebook: ${facebookOn ? 'On' : 'Off'}\nGoogle: ${googleOn ? 'On' : 'Off'}`;

                        return (
                            <Box
                                key={iso}
                                title={tooltip}
                                sx={{
                                    position: 'relative',
                                    aspectRatio: '1 / 1',
                                    borderRadius: '4px',
                                    bgcolor: bgColor,
                                    border: isToday ? '1.5px solid' : '1px solid',
                                    borderColor: isToday ? 'primary.500' : 'transparent',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: isFuture ? 'text.tertiary' : '#fff',
                                    textShadow: isFuture ? 'none' : '0 1px 1px rgba(0,0,0,0.25)',
                                    cursor: 'default',
                                    lineHeight: 1,
                                }}
                            >
                                <Typography
                                    level="body-xs"
                                    sx={{ fontSize: '11px', fontWeight: 600, color: 'inherit' }}
                                >
                                    {formatDateFns(cellDate, 'd')}
                                </Typography>
                                {platformLabel && !isFuture && (
                                    <Typography
                                        level="body-xs"
                                        sx={{ fontSize: '9px', fontWeight: 700, color: 'inherit', mt: '1px' }}
                                    >
                                        {platformLabel}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Sheet>
    );
};

export default function Home() {
    const [statistics, setStatistics] = useState<ApiStatisticData>(initialStatistics);
    const [marketingBalance, setMarketingBalance] = useState<MarketingBalanceSummary>(initialMarketingBalance);
    const [loadingData, setLoadingData] = useState(true);
    const [loadingMarketingBalance, setLoadingMarketingBalance] = useState(true);
    const [adsActivityMonth, setAdsActivityMonth] = useState<Date>(() => startOfMonth(new Date()));
    const [adsActivity, setAdsActivity] = useState<AdsActivityResponse | null>(null);
    const [loadingAdsActivity, setLoadingAdsActivity] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [showDatePickerModal, setShowDatePickerModal] = useState(false);

    const initialStartDate = useMemo(() => addDays(new Date(), -29), []);
    const initialEndDate = useMemo(() => new Date(), []);

    const [dateRange, setDateRange] = useState<DateRangeStateItem[]>([
        {
            startDate: initialStartDate,
            endDate: initialEndDate,
            key: 'selection'
        }
    ]);

    const [initialFetchDone, setInitialFetchDone] = useState(false);

    useEffect(() => {
        if (dateRange[0].startDate && dateRange[0].endDate) {
            setInitialFetchDone(true);
        }
    }, []);

    useEffect(() => {
        if (!initialFetchDone) {
            return;
        }

        const currentRange = dateRange[0];
        if (!currentRange.startDate || !currentRange.endDate) {
            setLoadingData(false);
            setError("Invalid date range selected.");
            return;
        }

        const abortController = new AbortController();

        setLoadingData(true);
        setError(null);

        const startDateToFetch = formatDateFns(currentRange.startDate, 'dd/MM/yyyy');
        const endDateToFetch = formatDateFns(currentRange.endDate, 'dd/MM/yyyy');

        const fetchData = async () => {
            const locations = [localStorage.getItem('location') || 'all'];

            try {
                const statisticsResponse = await ApiService.getStatistics(
                    locations,
                    startDateToFetch,
                    endDateToFetch,
                    abortController.signal
                );
                if (!abortController.signal.aborted) {
                    setStatistics(statisticsResponse);
                }
            } catch (err: any) {
                if (abortController.signal.aborted) {
                    return; // Request was cancelled, ignore
                }
                console.error("Error fetching statistics:", err);
                setError(err.response?.data?.error || "Failed to load statistics.");
                setStatistics(initialStatistics);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoadingData(false);
                }
            }
        };

        const fetchMarketingBalance = async () => {
            setLoadingMarketingBalance(true);
            try {
                const marketingBalanceResponse = await GeneralApiService.getAdBalanceChangesSummary(
                    startDateToFetch,
                    endDateToFetch
                );
                if (!abortController.signal.aborted) {
                    setMarketingBalance(marketingBalanceResponse);
                }
            } catch (err: any) {
                if (abortController.signal.aborted) {
                    return;
                }
                console.error("Error fetching marketing balance:", err);
                setMarketingBalance(initialMarketingBalance);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoadingMarketingBalance(false);
                }
            }
        };

        fetchData();
        fetchMarketingBalance();

        return () => {
            abortController.abort();
        };
    }, [dateRange, initialFetchDone]);

    useEffect(() => {
        if (currentTabIndex !== 5) {
            return;
        }

        let cancelled = false;
        const year = adsActivityMonth.getFullYear();
        const month = adsActivityMonth.getMonth() + 1;

        setLoadingAdsActivity(true);
        GeneralApiService.getAdsActivity(year, month)
            .then((response: AdsActivityResponse) => {
                if (!cancelled) {
                    setAdsActivity(response);
                }
            })
            .catch((err: any) => {
                if (!cancelled) {
                    console.error("Error fetching ads activity:", err);
                    setAdsActivity(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingAdsActivity(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [adsActivityMonth, currentTabIndex]);

    const handleDateChange = (item: RangeKeyDict) => {
        const selection = item.selection as Range;

        if (selection && selection.startDate && selection.endDate) {
            if (
                !dateRange[0].startDate || !dateRange[0].endDate ||
                formatDateFns(selection.startDate, 'yyyy-MM-dd') !== formatDateFns(dateRange[0].startDate, 'yyyy-MM-dd') ||
                formatDateFns(selection.endDate, 'yyyy-MM-dd') !== formatDateFns(dateRange[0].endDate, 'yyyy-MM-dd')
            ) {
                setDateRange([{
                    startDate: selection.startDate,
                    endDate: selection.endDate,
                    key: 'selection'
                }]);
            }
        }
    };

    const commonChartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
            }
        },
        scales: {
            x: {
                type: 'time',
                adapters: {
                    date: {
                        locale: enGB,
                    },
                },
                time: {
                    unit: 'day',
                    tooltipFormat: 'MMM dd, yyyy',
                },
                title: { display: true, text: 'Date' }
            },
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Value' }
            }
        }
    };

    const chartColors = [
        'rgba(255, 99, 132, 0.7)', 'rgba(54, 162, 235, 0.7)',
        'rgba(255, 206, 86, 0.7)', 'rgba(75, 192, 192, 0.7)',
        'rgba(153, 102, 255, 0.7)', 'rgba(255, 159, 64, 0.7)',
        'rgba(199, 199, 199, 0.7)', 'rgba(83, 102, 255, 0.7)',
        'rgba(40, 159, 64, 0.7)', 'rgba(210, 99, 132, 0.7)'
    ];
    const chartBorderColors = chartColors.map(color => color.replace('0.7', '1'));

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home"><HomeRoundedIcon /></Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>Statistics</Typography>
                </Breadcrumbs>
            </Box>
            <Typography level="h2" component="h1" mb={2}>Franchise Statistics</Typography>

            {/* Mobile Date Range Button */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
                <Button
                    variant="outlined"
                    color="neutral"
                    startDecorator={<CalendarMonthRounded />}
                    onClick={() => setShowDatePickerModal(true)}
                    sx={{ width: '100%' }}
                >
                    {dateRange[0].startDate && dateRange[0].endDate
                        ? `${formatDateFns(dateRange[0].startDate, 'dd MMM yyyy')} - ${formatDateFns(dateRange[0].endDate, 'dd MMM yyyy')}`
                        : 'Select Date Range'}
                </Button>
            </Box>

            {/* Mobile Date Range Modal */}
            <Modal open={showDatePickerModal} onClose={() => setShowDatePickerModal(false)}>
                <ModalDialog sx={{ width: '95vw', maxWidth: '400px', maxHeight: '90vh', overflow: 'auto', p: 2 }}>
                    <ModalClose />
                    <Typography level="title-lg" mb={2}>Select Date Range</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', '& .rdrCalendarWrapper': { width: '100%' } }}>
                        <DateRangePicker
                            onChange={(item) => {
                                handleDateChange(item);
                            }}
                            showSelectionPreview={true}
                            moveRangeOnFirstSelection={false}
                            months={1}
                            ranges={dateRange}
                            direction="vertical"
                            preventSnapRefocus={true}
                            calendarFocus="backwards"
                            locale={enGB}
                            staticRanges={customStaticRanges}
                        />
                    </Box>
                    <Button sx={{ mt: 2 }} onClick={() => setShowDatePickerModal(false)}>Apply</Button>
                </ModalDialog>
            </Modal>

            <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
                {/* Desktop Date Range Picker */}
                <Grid xs={12} md={4} lg={3} sx={{ display: { xs: 'none', md: 'flex' }, minWidth: 'fit-content' }}>
                    <Sheet
                        variant="outlined"
                        sx={{
                            minWidth: 'fit-content',
                            p: 1.5,
                            borderRadius: 'sm',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Typography level="title-md" mb={1} textAlign="center">Select Date Range</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <DateRangePicker
                                onChange={handleDateChange}
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                ranges={dateRange}
                                direction="vertical"
                                preventSnapRefocus={true}
                                calendarFocus="backwards"
                                locale={enGB}
                                staticRanges={customStaticRanges}
                            />
                        </Box>
                    </Sheet>
                </Grid>

                <Grid xs={12} md={8} lg={9} sx={{ display: 'flex' }}>
                    {!loadingData && !error && statistics.summary ? (
                        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Revenue (Period)" value={statistics.summary.totalRevenue.toFixed(2)} prefix="£" /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="New Roof Clean Bookings (Period)" value={statistics.summary.totalNewBookingsCreated} /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="New Roof Clean Revenue (Period)" value={statistics.summary.totalNewBookingsRevenue.toFixed(2)} prefix="£" /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Bookings Scheduled (Period)" value={statistics.summary.totalBookingsScheduled} /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Leads (Period)" value={statistics.summary.totalLeads} /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Conversion Rate (Period)" value={statistics.summary.conversionRate.toFixed(1)} suffix="%" /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Needs Rescheduling (Period)" value={statistics.summary.needsReschedulingJobsCount} /></Grid>
                            <Grid xs={6} sm={4} md={4} lg={3} sx={{ display: 'flex' }}><KpiCard title="Roof Clean Cancelled Jobs (Period)" value={statistics.summary.cancelledJobsCount} /></Grid>
                        </Grid>
                    ) : (
                        <Box sx={{ flexGrow: 1 }} />
                    )}
                </Grid>
                
            </Grid>

            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {loadingData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size="lg" />
                    </Box>
                ) : error ? (
                    <Sheet color="danger" variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                        <Typography textColor="danger.700">{error}</Typography>
                    </Sheet>
                ) : (
                    <Tabs
                        aria-label="Statistics tabs"
                        value={currentTabIndex}
                        onChange={(event, newValue) => setCurrentTabIndex(newValue as number)}
                        sx={{ bgcolor: 'background.body', flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        <TabList
                            disableUnderline
                            sx={{ 
                                [`& .${tabClasses.root}[aria-selected="true"]`]: { boxShadow: 'sm', bgcolor: 'background.surface' },
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                            }}
                        >
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Overview</Tab>
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Revenue Details</Tab>
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Lead Details</Tab>
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Booking Details</Tab>
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Call Details</Tab>
                            <Tab disableIndicator sx={{ whiteSpace: 'nowrap', minWidth: 'fit-content' }}>Marketing Balance</Tab>
                        </TabList>

                        {[0, 1, 2, 3, 4, 5].map(tabIndex => (
                            <TabPanel
                                key={tabIndex}
                                value={tabIndex}
                                sx={{ p: { xs: 1, md: 2 }, flexGrow: 1, overflowY: 'auto', minHeight: 0 }}
                            >
                                {currentTabIndex === 0 && tabIndex === 0 && (
                                    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                        <Grid xs={6} sm={4} md={3}><KpiCard title="Roof Clean Jobs Scheduled (Period)" value={statistics.summary.totalBookingsScheduled} /></Grid>
                                        <Grid xs={6} sm={4} md={3}><KpiCard title="Avg. Roof Clean Job Value (Period)" value={statistics.summary.avgBookingValue.toFixed(2)} prefix="£" /></Grid>
                                        <Grid xs={6} sm={4} md={3}><KpiCard title="Potential Roof Clean Lead Value (Period)" value={statistics.summary.leadsPotentialRevenue.toFixed(2)} prefix="£" /></Grid>
                                        <Grid xs={6} sm={4} md={3}><KpiCard title="New Messages (Period)" value={statistics.summary.newMessages} /></Grid>
                                        <Grid xs={12} md={6}><ChartCard title="Daily Roof Clean Revenue Trend (from Scheduled Jobs)"><Line options={{...commonChartOptions, scales: { ...commonChartOptions.scales, y: { ...commonChartOptions.scales.y, title: { display: true, text: 'Revenue (£)'}}}}} data={{ labels: statistics.revenueDetails.dailyTrend.map(d => d.date), datasets: [{ label: 'Revenue', data: statistics.revenueDetails.dailyTrend.map(d => d.value), borderColor: chartBorderColors[3], backgroundColor: chartColors[3], tension: 0.1, fill: true, }]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6}>
                                            {(() => {
                                                const unifiedChartLabels = Array.from(new Set([...statistics.leadDetails.dailyTrend.map(d => d.date), ...statistics.bookingDetails.dailyTrend.map(d => d.date)])).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
                                                const leadDataMap = new Map(statistics.leadDetails.dailyTrend.map(d => [d.date, d.count]));
                                                const unifiedLeadData = unifiedChartLabels.map(date => leadDataMap.get(date) ?? null);
                                                const bookingDataMap = new Map(statistics.bookingDetails.dailyTrend.map(d => [d.date, d.count]));
                                                const unifiedBookingData = unifiedChartLabels.map(date => bookingDataMap.get(date) ?? null);
                                                return <ChartCard title="Daily Roof Clean Leads vs New Bookings Made"><Line options={{...commonChartOptions, scales: { x: { ...commonChartOptions.scales.x }, yLeads: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Leads Created Count' }, beginAtZero: true }, yBookings: { type: 'linear', display: true, position: 'right', title: { display: true, text: 'Bookings Created Count' }, grid: { drawOnChartArea: false }, beginAtZero: true }}}} data={{ labels: unifiedChartLabels, datasets: [{ label: 'Leads Created', data: unifiedLeadData, borderColor: chartBorderColors[1], backgroundColor: chartColors[1], yAxisID: 'yLeads', tension: 0.1, spanGaps: false }, { label: 'Bookings Created', data: unifiedBookingData, borderColor: chartBorderColors[0], backgroundColor: chartColors[0], yAxisID: 'yBookings', tension: 0.1, spanGaps: false }]}} /></ChartCard>
                                            })()}
                                        </Grid>
                                    </Grid>
                                )}
                                {currentTabIndex === 1 && tabIndex === 1 && (
                                    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                        <Grid xs={12} md={7}><ChartCard title="Revenue by Service (from Scheduled Jobs)"><Bar options={{...commonChartOptions, indexAxis: 'y' as const, scales: { x: { ...commonChartOptions.scales.x, type: 'linear', title: {display: true, text: 'Revenue (£)'}}, y: { ...commonChartOptions.scales.y, type: 'category', title: {display: true, text: 'Service'}}}}} data={{ labels: statistics.revenueDetails.byService.map(s => s.service_name), datasets: [{ label: 'Total Revenue (£)', data: statistics.revenueDetails.byService.map(s => s.total_revenue), backgroundColor: chartColors[3], borderColor: chartBorderColors[3], borderWidth: 1}]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={5}><ChartCard title="Revenue by Payment Type (from Scheduled Jobs)"><Pie options={{...commonChartOptions, plugins: {...commonChartOptions.plugins, legend: {position: 'right'}}, scales: {x: {display: false}, y: {display: false}} }} data={{ labels: statistics.revenueDetails.byPaymentType.map(p => p.payment_type_name), datasets: [{ label: 'Revenue by Payment Type', data: statistics.revenueDetails.byPaymentType.map(p => p.total_revenue), backgroundColor: chartColors, borderColor: chartBorderColors, borderWidth: 1}]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6} mt={2}><KpiCard title="Active Recurring Revenue (All Time)" value={statistics.summary.activeRecurringRevenueTotal.toFixed(2)} prefix="£" /></Grid>
                                        <Grid xs={6} md={3} mt={2}><KpiCard title="New Recurring Plans (Period)" value={statistics.summary.newRecurringBookingsCount} /></Grid>
                                        <Grid xs={6} md={3} mt={2}><KpiCard title="New Recurring Value (Period)" value={statistics.summary.newRecurringBookingsValue.toFixed(2)} prefix="£" /></Grid>
                                    </Grid>
                                )}
                                {currentTabIndex === 2 && tabIndex === 2 && (
                                    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                        <Grid xs={12} md={6}><ChartCard title="Roof Clean Leads by Advertising Platform"><Bar options={{...commonChartOptions, indexAxis: 'y' as const, scales: { x: { ...commonChartOptions.scales.x, type:'linear', title: {display: true, text: 'Number of Leads'}}, y: { ...commonChartOptions.scales.y, type:'category', title: {display: true, text: 'Platform'}}}}} data={{ labels: statistics.leadDetails.byAdvertisingPlatform.map(p => p.platform_name), datasets: [ { label: 'Total Leads', data: statistics.leadDetails.byAdvertisingPlatform.map(p => p.count), backgroundColor: chartColors[1], borderColor: chartBorderColors[1], }, { label: 'Converted Leads', data: statistics.leadDetails.byAdvertisingPlatform.map(p => p.converted_count), backgroundColor: chartColors[4], borderColor: chartBorderColors[4], } ]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6}><ChartCard title="Roof Clean Leads by Form Type"><Bar options={{...commonChartOptions, indexAxis: 'y' as const, scales: { x: { ...commonChartOptions.scales.x, type:'linear', title: {display: true, text: 'Number of Leads'}}, y: { ...commonChartOptions.scales.y, type:'category', title: {display: true, text: 'Form Type'}}}}} data={{ labels: statistics.leadDetails.byFormType.map(f => f.form_type), datasets: [ { label: 'Total Leads', data: statistics.leadDetails.byFormType.map(f => f.count), backgroundColor: chartColors[5], borderColor: chartBorderColors[5], }, { label: 'Converted Leads', data: statistics.leadDetails.byFormType.map(f => f.converted_count), backgroundColor: chartColors[8], borderColor: chartBorderColors[8], } ]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6} mt={2}><ChartCard title="Roof Clean Lead Conversion Sources"><Pie options={{...commonChartOptions, plugins: {...commonChartOptions.plugins, legend: {position: 'right'}}, scales: {x: {display: false}, y: {display: false}} }} data={{ labels: statistics.leadDetails.conversionSources.map(s => s.source), datasets: [{ label: 'Count', data: statistics.leadDetails.conversionSources.map(s => s.count), backgroundColor: chartColors, borderColor: chartBorderColors }]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6} mt={2}><KpiCard title="Potential Revenue from Roof Clean Leads" value={statistics.summary.leadsPotentialRevenue.toFixed(2)} prefix="£" /></Grid>
                                    </Grid>
                                )}
                                {currentTabIndex === 3 && tabIndex === 3 && (
                                    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                        <Grid xs={12} md={6}><ChartCard title="Roof Clean Bookings by Job Status (for Scheduled Jobs)"><Pie options={{...commonChartOptions, plugins: {...commonChartOptions.plugins, legend: {position: 'right'}}, scales: {x: {display: false}, y: {display: false}} }} data={{ labels: statistics.bookingDetails.byJobStatus.map(s => s.status), datasets: [{ label: 'Count', data: statistics.bookingDetails.byJobStatus.map(s => s.count), backgroundColor: chartColors, borderColor: chartBorderColors }]}} /></ChartCard></Grid>
                                        <Grid xs={12} md={6}><KpiCard title="Roof Clean Completion Rate (for Scheduled Jobs)" value={statistics.bookingDetails.completionRate.toFixed(1)} suffix="%" /></Grid>
                                        <Grid xs={6} md={6} mt={2}><KpiCard title="Gutter Certs Sent / Eligible" value={`${statistics.operationalMetrics.gutterCerts.sent} / ${statistics.operationalMetrics.gutterCerts.total_eligible}`} /></Grid>
                                        <Grid xs={6} md={6} mt={2}><KpiCard title="Review Requests Enabled / Completed" value={`${statistics.operationalMetrics.reviewRequests.enabled} / ${statistics.operationalMetrics.reviewRequests.total_completed}`} /></Grid>
                                    </Grid>
                                )}
                                {currentTabIndex === 4 && tabIndex === 4 && (
                                    <>
                                        <Typography size={"xs"} sx={{mb: 1}}>* Data older than 6th September 2025 may be incorrect.</Typography>
                                        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Total Outbound Calls" value={statistics.callMetrics?.totalOutboundCalls ?? 0} />
                                            </Grid>
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Total Inbound Calls" value={statistics.callMetrics?.totalInboundCalls ?? 0} />
                                            </Grid>
                                            <Grid xs={6} md={3}>
                                                <KpiCard
                                                    title="Avg Speed to Lead"
                                                    value={statistics.callMetrics?.avgSpeedToLeadSeconds != null ? (statistics.callMetrics.avgSpeedToLeadSeconds / 60).toFixed(1) : 'N/A'}
                                                    suffix=" mins"
                                                />
                                            </Grid>
                                        </Grid>
                                    </>

                                )}
                                {currentTabIndex === 5 && tabIndex === 5 && (
                                    loadingMarketingBalance ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                            <CircularProgress size="lg" />
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                                            {/* Summary KPI Cards */}
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Total Charges" value={marketingBalance.totals.charges.total.toFixed(2)} prefix="£" />
                                            </Grid>
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Charge Count" value={marketingBalance.totals.charges.count} />
                                            </Grid>
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Total Top Ups" value={marketingBalance.totals.top_ups.total.toFixed(2)} prefix="£" />
                                            </Grid>
                                            <Grid xs={6} md={3}>
                                                <KpiCard title="Top Up Count" value={marketingBalance.totals.top_ups.count} />
                                            </Grid>

                                            {/* Summary by Type */}
                                            <Grid xs={12} md={6}>
                                                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                                                    <Typography level="title-md" mb={2}>Summary by Type</Typography>
                                                    {marketingBalance.by_type.length > 0 ? (
                                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <Box component="thead">
                                                                <Box component="tr">
                                                                    <Box component="th" sx={{ textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Type</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Amount</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Count</Box>
                                                                </Box>
                                                            </Box>
                                                            <Box component="tbody">
                                                                {marketingBalance.by_type.map((item, idx) => (
                                                                    <Box component="tr" key={idx} sx={{ bgcolor: item.type === 'Charge' ? 'rgba(255, 0, 0, 0.05)' : 'rgba(0, 255, 0, 0.05)' }}>
                                                                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.type}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>£{item.total_amount.toFixed(2)}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.count}</Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography textColor="text.secondary">No data for the selected period</Typography>
                                                    )}
                                                </Sheet>
                                            </Grid>

                                            {/* Summary by Service */}
                                            <Grid xs={12} md={6}>
                                                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                                                    <Typography level="title-md" mb={2}>Summary by Service</Typography>
                                                    {marketingBalance.by_service.filter(item => item.service).length > 0 ? (
                                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <Box component="thead">
                                                                <Box component="tr">
                                                                    <Box component="th" sx={{ textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Service</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Amount</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Count</Box>
                                                                </Box>
                                                            </Box>
                                                            <Box component="tbody">
                                                                {marketingBalance.by_service.filter(item => item.service).map((item, idx) => (
                                                                    <Box component="tr" key={idx}>
                                                                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.service}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>£{item.total_amount.toFixed(2)}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.count}</Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                            <Box component="tfoot">
                                                                <Box component="tr">
                                                                    <Box component="td" sx={{ p: 1, borderTop: '2px solid', borderColor: 'divider', fontWeight: 'bold' }}>Total</Box>
                                                                    <Box component="td" sx={{ textAlign: 'right', p: 1, borderTop: '2px solid', borderColor: 'divider', fontWeight: 'bold' }}>
                                                                        £{marketingBalance.by_service.filter(item => item.service).reduce((sum, item) => sum + (item.service === 'Lead Conversion Refund' ? -item.total_amount : item.total_amount), 0).toFixed(2)}
                                                                    </Box>
                                                                    <Box component="td" sx={{ textAlign: 'right', p: 1, borderTop: '2px solid', borderColor: 'divider', fontWeight: 'bold' }}>
                                                                        {marketingBalance.by_service.filter(item => item.service).reduce((sum, item) => sum + item.count, 0)}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography textColor="text.secondary">No data for the selected period</Typography>
                                                    )}
                                                </Sheet>
                                            </Grid>

                                            {/* Detailed Breakdown by Type and Service */}
                                            <Grid xs={12}>
                                                <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm' }}>
                                                    <Typography level="title-md" mb={2}>Breakdown by Type & Service</Typography>
                                                    {marketingBalance.by_type_and_service.filter(item => item.service).length > 0 ? (
                                                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                                                            <Box component="thead">
                                                                <Box component="tr">
                                                                    <Box component="th" sx={{ textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Type</Box>
                                                                    <Box component="th" sx={{ textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Service</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Amount</Box>
                                                                    <Box component="th" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>Count</Box>
                                                                </Box>
                                                            </Box>
                                                            <Box component="tbody">
                                                                {marketingBalance.by_type_and_service.filter(item => item.service).map((item, idx) => (
                                                                    <Box component="tr" key={idx} sx={{ bgcolor: item.type === 'Charge' ? 'rgba(255, 0, 0, 0.05)' : 'rgba(0, 255, 0, 0.05)' }}>
                                                                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.type}</Box>
                                                                        <Box component="td" sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.service}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>£{item.total_amount.toFixed(2)}</Box>
                                                                        <Box component="td" sx={{ textAlign: 'right', p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>{item.count}</Box>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Typography textColor="text.secondary">No data for the selected period</Typography>
                                                    )}
                                                </Sheet>
                                            </Grid>

                                            {/* Ads Activity Calendar */}
                                            <Grid xs={12}>
                                                <AdsActivityCalendar
                                                    month={adsActivityMonth}
                                                    onMonthChange={setAdsActivityMonth}
                                                    data={adsActivity}
                                                    loading={loadingAdsActivity}
                                                />
                                            </Grid>
                                        </Grid>
                                    )
                                )}

                            </TabPanel>
                        ))}
                    </Tabs>
                )}
            </Box>
        </>
    );
}